import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Circle,
  PauseCircle,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  AlertTriangle,
  ExternalLink,
  X,
} from "lucide-react";

import { Button } from "nazare-ui/primitives";
import { CollapsibleSection, SingleSelectDropdown, WorkflowProgressSummary, type WorkflowProgressItem } from "nazare-ui/components";
import { cn } from "nazare-ui/utils";

import { useSecondaryHeader } from "../../../../secondary-header-context";
import { SkeletonBlock } from "../../../../skeleton-block";
import { useDocumentTitle } from "../../../../use-document-title";
import { workflowRunLoadErrorMessage } from "../../../../workflow-run-load-error";
import { isRetryableWorkflowRunStatus, workflowRunPauseState, workflowRunConfirmedPaused } from "../../../../workflow-run-control";
import { readWorkflowStepStatuses, withWorkflowStepStatuses, workflowRunTabHref, type WorkflowRunTab as Tab } from "../../../../workflow-step-query";
import { WorkflowModal } from "../workflow-run-modal";
import {
  DEFAULT_WORKFLOW_SOURCE,
  isActiveWorkflowStatus,
  useCancelWorkflowRun,
  useRetryWorkflowRun,
  useWorkflowDefinitions,
  useWorkflowRunDetail,
  useWorkflowRunDetailStream,
  useWorkflowRunEvents,
  useWorkflowRunProgress,
  useWorkflowRunStepBranch,
  useWorkflowRunSteps,
  type DiagnosticCounts,
  type DiagnosticSummary,
  type EventSummary,
  type RunDetailTree,
  type RunSummary,
  type StepSummary,
  type WorkflowProgressResponse,
} from "../../../../workflows-api";
import { ArtefactsPanel } from "./run-detail-artefacts-panel";
import { DiagnosticChips, DiagnosticRow, SeverityChip } from "./run-detail-diagnostics";
import { elapsedMs, formatDuration, formatTimestamp, runClockMs, runElapsedMs, sentryEventUrl, toMs } from "./run-detail-format";
import { LogsTab } from "./run-detail-logs-tab";
import { ImmortalChip, StatusChip, TriggerChip } from "./run-detail-status-chip";
import { useNow } from "./use-now";
import { RunPauseControl } from "./run-pause-control";
import { RetryFailedStepsControl } from "./retry-failed-steps-control";
import { WorkflowFilterDropdown, type WorkflowFilterOption } from "../runs-table";
import { workflowProgressLines, workflowProgressRowLines, workflowProgressValueEntries } from "../../../../workflow-progress";
import { compareWorkflowCreation, compareWorkflowStepsByCreation, compareWorkflowStepsNewestFirst, workflowTreeOrder } from "../../../../workflow-step-order";
import { shouldAutoLoadWorkflowStepPage } from "../../../../workflow-step-pagination";
import { WorkflowStepRecoveryScheduler } from "../../../../workflow-step-recovery";
import { stepDisplayLabel, workflowDisplayLabel } from "../display-labels";
import {
  filterWorkflowTreeStepsByStatus,
  mergeWorkflowTreePages,
  WORKFLOW_TREE_CHILD_LOADING_CLASS,
  workflowTreeChildLoadingPadding,
  workflowTreeRootSteps,
  workflowVisibleBranchKeys,
} from "../../../../workflow-progressive-tree";
import {
  mergeLatestWorkflowSteps,
  aggregateLatestWorkflowNodeStatuses,
  workflowTreePathKey,
  mergeLiveWorkflowSteps,
  emptyWorkflowStepExpansion,
  WORKFLOW_RUN_TREE_KEY,
  workflowStepExpanded,
  withWorkflowStepDescendantExpansion,
  displayWorkflowStepStatus,
  matchesWorkflowStepStatus,
  projectCancelledWorkflowStep,
  untimedWorkflowStepLabel,
  withWorkflowStepExpansionOverride,
  type WorkflowStepExpansion,
} from "../../../../workflow-step-filter";
import styles from "./run-detail.module.css";

/**
 * Full-parity port of nazare-frontend's
 * `screens/admin/workflows/run-detail.tsx` (the "Observer"-style trace
 * Gantt, diagnostics feed, artefacts panel and logs tab). Ported behaviour-
 * for-behaviour; the only deliberate departures are platform-native
 * plumbing, not feature cuts:
 *   - REST hooks come from `../../../../workflows-api` (already a near-1:1
 *     port of the core app's own `api.ts` against the same BFF routes).
 *   - `Button`/`Dialog` are `nazare-ui` primitives, not the core app's own
 *     component kit / mobx `modalStore`+`ConfirmModal`.
 *   - No toast library — a failed retry surfaces as an inline error instead.
 *   - There's only one World per environment here too, so `source` is the
 *     constant `DEFAULT_WORKFLOW_SOURCE`, not a `useSource()` indirection.
 */

const SOURCE = DEFAULT_WORKFLOW_SOURCE;
const RUN_HEADER_EXPANDED_STORAGE_KEY = "nzr.workflow.runHeader.expanded";

/** Terminal statuses — a run in one of these can no longer be cancelled. */
const TERMINAL_STATUSES = new Set(["completed", "failed", "timed_out", "cancelled"]);
const isCancellable = (status: string) => !TERMINAL_STATUSES.has(status.toLowerCase());

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "timeline", label: "Timeline" },
  { id: "diagnostics", label: "Diagnostics" },
  { id: "artefacts", label: "Artefacts" },
  { id: "logs", label: "Logs" },
];

  const STEP_STATUS_OPTIONS: WorkflowFilterOption[] = [
  { value: "queued", label: "Queued" },
  { value: "running", label: "Running" },
  { value: "paused", label: "Paused" },
  { value: "waiting", label: "Waiting" },
  { value: "sleeping", label: "Sleeping" },
  { value: "completed", label: "Completed" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
  { value: "timed_out", label: "Timed out" },
  { value: "cancelled", label: "Cancelled" },
  { value: "interrupted", label: "Interrupted" },
  { value: "awaiting_manual_review", label: "Awaiting manual review" },
];

function HeaderField({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cn("flex flex-col gap-0.5", wide && "col-span-2")}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--nzr-muted)]">{label}</span>
      <span className="text-[13px] text-[var(--ink)] tabular-nums break-all">{value}</span>
    </div>
  );
}

function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  pending,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  pending: boolean;
  error?: string | null;
}) {
  return (
    open ? <WorkflowModal
      title={title}
      description={description}
      error={error}
      confirmLabel={confirmLabel}
      pending={pending}
      onClose={() => onOpenChange(false)}
      onConfirm={() => onConfirm()}
    /> : null
  );
}

/**
 * Cancel button for an in-flight run. Shown only while the run is active
 * (running/paused). Immortal workflows stop after their current cycle and
 * then release the singleton slot.
 */
function CancelRunButton({ runId, status }: { runId: string; status: string }) {
  const cancel = useCancelWorkflowRun(SOURCE);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!isCancellable(status)) return null;
  return (
    <>
      <Button className={styles.workflowButton} variant="outline" onClick={() => setOpen(true)}>
        Cancel run
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setError(null);
        }}
        title="Cancel this run?"
        description="An immortal workflow stops after its current cycle."
        confirmLabel="Cancel run"
        pending={cancel.isPending}
        error={error}
        onConfirm={async () => {
          setError(null);
          try {
            await cancel.mutateAsync(runId);
            setOpen(false);
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not cancel run");
          }
        }}
      />
    </>
  );
}

/** The run-detail URL for a run id. */
function runDetailHref(runId: string): string {
  return `/workflows/runs/${encodeURIComponent(runId)}`;
}

/**
 * Retry button for a FINISHED run. Dispatches a new run of the same
 * workflow with the same input (the next attempt in the lineage) and
 * navigates to it. Shown only for an unsuccessful terminal run.
 */
function RetryRunButton({ runId, status }: { runId: string; status: string }) {
  const navigate = useNavigate();
  const retry = useRetryWorkflowRun(SOURCE);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!isRetryableWorkflowRunStatus(status)) return null;
  return (
    <>
      <Button className={styles.workflowButton} variant="outline" onClick={() => setOpen(true)}>
        Retry run
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setError(null);
        }}
        title="Retry this run?"
        description="A new run is dispatched with the same input."
        confirmLabel="Retry run"
        pending={retry.isPending}
        error={error}
        onConfirm={async () => {
          setError(null);
          try {
            const result = await retry.mutateAsync(runId);
            setOpen(false);
            navigate(runDetailHref(result.runId));
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not retry run");
          }
        }}
      />
    </>
  );
}

function progressStatusLabel(status: string): string {
  return status.replace(/(^|[-_ ])([a-z])/g, (_, separator: string, letter: string) => `${separator}${letter.toUpperCase()}`);
}

function progressEntryStatus(run: RunSummary, entry: WorkflowProgressResponse["snapshots"][number]): string {
  const status = entry.metadata?.status;
  if (entry.metadata?.inheritedFromRunId && typeof status !== "string") return "Saved";
  return progressStatusLabel(typeof status === "string" ? status : run.status);
}

function workflowProgressItems(run: RunSummary, progress: WorkflowProgressResponse | null | undefined): WorkflowProgressItem[] {
  if (!progress) return [];
  return progress.snapshots.map((entry, index) => ({
    label: entry.metadata?.inheritedFromRunId
      ? `${entry.scopeKey ?? entry.stepId ?? "Workflow"} (saved progress)`
      : entry.scope === "step"
      ? entry.scopeKey ?? entry.stepId
      : entry.scopeKey && entry.scopeKey !== run.runId
        ? entry.scopeKey
        : index === 0 ? "Workflow" : "Workflow progress",
    status: progressEntryStatus(run, entry),
    updatedAt: formatTimestamp(entry.updatedAt),
    values: workflowProgressValueEntries(entry.payload, entry.metadata),
  }));
}

function RunHeader({
  run,
  counts,
  stepStatusCounts,
  durableWaitCounts,
  workflowProgress,
  workflowProgressLoading,
  workflowProgressError,
  workflowProgressRetry,
  now,
  definition,
}: {
  run: RunSummary;
  counts: DiagnosticCounts | null | undefined;
  stepStatusCounts: Record<string, number> | null;
  durableWaitCounts: { sleeping: number; ready: number } | null;
  workflowProgress: WorkflowProgressResponse | null | undefined;
  workflowProgressLoading: boolean;
  workflowProgressError: boolean;
  workflowProgressRetry: () => void;
  now: number;
  definition: { label: string; description: string; immortal?: boolean } | null;
}) {
  const duration = runElapsedMs(run, now);
  return (
    <CollapsibleSection
      className={cn(styles.surface, "p-4 flex flex-col gap-4")}
      contentClassName="flex flex-col gap-4"
      storageKey={RUN_HEADER_EXPANDED_STORAGE_KEY}
      summary={
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-[16px] font-semibold text-[var(--ink)] truncate">
            {definition?.label ?? workflowDisplayLabel(run)}
          </h2>
          {run.label && definition?.label ? (
            <span className="text-[13px] text-[var(--nzr-muted)] truncate max-w-[16rem]" title={run.label}>
              {run.label}
            </span>
          ) : null}
          <StatusChip
            status={run.status}
            pauseState={workflowRunPauseState(run)}
          />
          <TriggerChip trigger={run.triggeredBy} />
          <ImmortalChip immortal={definition?.immortal} />
          <DiagnosticChips counts={counts} />
          <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
            <RunPauseControl key={run.runId} run={run} />
            <RetryFailedStepsControl key={`retry-failed:${run.runId}`} run={run} />
            <CancelRunButton runId={run.runId} status={run.status} />
            <RetryRunButton runId={run.runId} status={run.status} />
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-1">
        {definition?.description && <p className="text-[12px] text-[var(--nzr-muted)]">{definition.description}</p>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <HeaderField label="Run ID" value={<span className="font-mono">{run.runId}</span>} />
        <HeaderField label="Deployment" value={run.deploymentId ?? "—"} />
        <HeaderField label="Duration" value={duration === null ? "—" : formatDuration(duration)} />
        <HeaderField label="Created" value={formatTimestamp(run.createdAt)} />
        <HeaderField label="Started" value={formatTimestamp(run.startedAt)} />
        <HeaderField label="Completed" value={formatTimestamp(run.completedAt)} />
      </div>
      {stepStatusCounts ? <div className="border-t border-[var(--border-soft)] pt-3">
        <div className="mb-2 text-[12px] text-[var(--nzr-muted)]">
          <span className="font-medium text-[var(--ink)]">Queued workflow steps</span>{" "}
          <span className="tabular-nums">{((stepStatusCounts.pending ?? 0) + (stepStatusCounts.queued ?? 0)).toLocaleString("en-GB")} steps</span>
        </div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--nzr-muted)]">Workflow steps <span className="font-normal normal-case">({Object.values(stepStatusCounts).reduce((total, count) => total + count, 0).toLocaleString("en-GB")} total)</span></div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {Object.entries(stepStatusCounts).map(([status, count]) => (
            <span key={status} className="text-[12px] text-[var(--nzr-muted)]">
              <span className="font-medium text-[var(--ink)]">{count.toLocaleString("en-GB")}</span> {status.replace(/(^|[-_ ])([a-z])/g, (_, separator: string, letter: string) => `${separator}${letter.toUpperCase()}`)}
            </span>
          ))}
        </div>
      </div> : <div className="border-t border-[var(--border-soft)] pt-3 text-[12px] text-[var(--nzr-muted)]">Queued workflow steps calculating…</div>}
      {durableWaitCounts ? <div className="text-[12px] text-[var(--nzr-muted)]"><span className="font-medium text-[var(--ink)]">{durableWaitCounts.sleeping.toLocaleString("en-GB")}</span> Sleeping waits <span className="ml-4 font-medium text-[var(--ink)]">{durableWaitCounts.ready.toLocaleString("en-GB")}</span> Ready waits</div> : <div className="text-[12px] text-[var(--nzr-muted)]">Durable waits calculating…</div>}
      <WorkflowProgressSummary
        coverage={workflowProgress?.coverage.state ?? "warming"}
        items={workflowProgressItems(run, workflowProgress)}
        loading={workflowProgressLoading}
        error={workflowProgressError}
        unavailable={workflowProgressError && !workflowProgress}
        onRetry={workflowProgressRetry}
      />
    </CollapsibleSection>
  );
}

/**
 * Normalise an opaque run/step error into a headline message + optional
 * code + the full JSON for the details drawer.
 */
function describeError(error: unknown): { message: string; code: string | null; raw: string | null } {
  if (error === null || error === undefined) return { message: "", code: null, raw: null };
  if (typeof error === "string") return { message: error, code: null, raw: null };
  if (typeof error === "object") {
    const obj = error as Record<string, unknown>;
    const message = typeof obj.message === "string" ? obj.message : JSON.stringify(error);
    const code = typeof obj.code === "string" ? obj.code : null;
    const raw = JSON.stringify(error, null, 2);
    return { message, code, raw: raw === JSON.stringify(message) ? null : raw };
  }
  return { message: String(error), code: null, raw: null };
}

function sentryEventIdOf(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const id = (error as Record<string, unknown>).sentryEventId;
  return typeof id === "string" ? id : null;
}

/** Inline red error panel for a failed step's captured error. */
function ErrorBlock({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null;
  const { message, code, raw } = describeError(error);
  const sentryEventId = sentryEventIdOf(error);
  return (
    <div className="rounded-sm border border-[var(--red)] bg-[var(--red-bg)] px-3 py-2">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[var(--red)]" />
        <div className="min-w-0">
          {code && (
            <span className="mr-2 rounded-xs bg-[var(--card-2)] px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-[var(--red)]">
              {code}
            </span>
          )}
          {sentryEventId && (
            <a
              href={sentryEventUrl(sentryEventId)}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-2 inline-flex items-center gap-1 rounded-xs border border-[var(--red)] bg-[var(--nzr-card)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--red)] hover:bg-[var(--card-2)]"
            >
              <ExternalLink size={11} />
              Sentry
            </a>
          )}
          <span className="text-[12px] text-[var(--red)] break-words">{message}</span>
          {raw && <pre className="mt-1.5 max-h-48 overflow-auto whitespace-pre-wrap text-[11px] text-[var(--red)]/80">{raw}</pre>}
        </div>
      </div>
    </div>
  );
}

/** Prominent failure banner shown above the tabs whenever a run ended `failed`. */
function RunFailureBanner({ run }: { run: RunSummary }) {
  const isFailed = run.status.toLowerCase() === "failed";
  const hasError = run.error !== null && run.error !== undefined;
  if (!isFailed && !hasError) return null;
  const { message, code, raw } = describeError(run.error);
  return (
    <section className="rounded-sm border border-[var(--red)] bg-[var(--red-bg)] p-4">
      <div className="flex items-start gap-3">
        <XCircle size={18} className="mt-0.5 shrink-0 text-[var(--red)]" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-[var(--red)]">Run failed</span>
            {code && (
              <span className="rounded-xs bg-[var(--card-2)] px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-[var(--red)]">
                {code}
              </span>
            )}
            {run.sentryEventId && (
              <a
                href={sentryEventUrl(run.sentryEventId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-xs border border-[var(--red)] bg-[var(--nzr-card)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--red)] hover:bg-[var(--card-2)]"
              >
                <ExternalLink size={11} />
                View in Sentry
              </a>
            )}
          </div>
          <p className="mt-0.5 text-[13px] text-[var(--red)] break-words">{message || "This run ended in a failed state."}</p>
          {raw && <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-[11px] text-[var(--red)]/80">{raw}</pre>}
        </div>
      </div>
    </section>
  );
}

/** Status glyph for a timeline step node. */
function StatusIcon({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === "completed" || s === "succeeded") return <CheckCircle2 size={18} className="text-[var(--green)]" />;
  if (s === "failed") return <XCircle size={18} className="text-[var(--red)]" />;
  if (s === "running") return <Loader2 size={18} className="text-[var(--blue)] animate-spin" />;
  if (s === "paused") return <PauseCircle size={18} className="text-[var(--orange)]" />;
  if (s === "pending") return <Clock size={18} className="text-[var(--nzr-muted)]" />;
  return <Circle size={18} className="text-[var(--nzr-muted)]" />;
}

/** One node on the vertical timeline rail. */
function TimelineNode({ icon, isLast, children }: { icon: React.ReactNode; isLast: boolean; children: React.ReactNode }) {
  return (
    <li className="relative flex gap-3">
      {!isLast && <span className="absolute left-[10px] top-6 bottom-0 w-px bg-[var(--border-soft)]" aria-hidden />}
      <span className="relative z-10 flex h-5 w-5 items-center justify-center bg-[var(--nzr-card)] shrink-0">{icon}</span>
      <div className="min-w-0 flex-1 pb-5">{children}</div>
    </li>
  );
}

/** A single nested log line (a step-bound event) inside a step accordion. */
function LogLine({ event }: { event: EventSummary }) {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <span className="block h-1.5 w-1.5 rounded-full bg-[var(--border-soft)] shrink-0" aria-hidden />
      <span className="font-mono text-[11px] text-[var(--nzr-muted)] tabular-nums shrink-0">{formatTimestamp(event.createdAt)}</span>
      <span className="font-mono text-[12px] text-[var(--ink)] truncate">{event.eventType}</span>
    </div>
  );
}

function stepEndAt(step: StepSummary): string | null {
  return isActiveWorkflowStatus(step.status) ? null : step.updatedAt;
}

function stepEndMs(step: StepSummary, now: number): number {
  return isActiveWorkflowStatus(step.status) ? now : (toMs(step.updatedAt) ?? now);
}

function displayStepStatus(step: StepSummary, parentStatus: string) { return displayWorkflowStepStatus(step.status, parentStatus); }

/** A step on the Diagnostics feed: status glyph, name, status pill, expandable accordion. */
function StepNode({
  step,
  logs,
  diagnostics,
  now,
  isLast,
  open,
  onOpenChange,
  parentStatus,
  processingPaused,
}: {
  step: StepSummary;
  logs: EventSummary[];
  diagnostics: DiagnosticSummary[];
  now: number;
  isLast: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  parentStatus: string;
  processingPaused: boolean;
}) {
  const display = displayStepStatus(step, parentStatus);
  const visualStatus = processingPaused && isActiveWorkflowStatus(step.status) ? "paused" : display.status;
  const hasError = step.error !== null && step.error !== undefined;
  const progressLines = workflowProgressLines(step.progress);
  const rowProgressLines = workflowProgressRowLines(step.progress);
  const hasDetail = hasError || logs.length > 0 || diagnostics.length > 0 || progressLines.length > 0;
  const duration = elapsedMs(step.startedAt, stepEndAt(step), now);
  return (
    <TimelineNode icon={<StatusIcon status={visualStatus} />} isLast={isLast}>
      <div className={cn("-mt-0.5 rounded-sm border border-[var(--nzr-border)]", open && hasDetail ? "bg-[var(--card-2)]" : "bg-[var(--nzr-card)]")}>
        <button
          type="button"
          onClick={() => hasDetail && onOpenChange(!open)}
          className={cn("flex w-full items-start gap-2 px-3 py-2 text-left", hasDetail ? "cursor-pointer" : "cursor-default")}
        >
          {hasDetail && (
            <ChevronRight size={14} className={cn("shrink-0 text-[var(--nzr-muted)] transition-transform", open && "rotate-90")} />
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-medium text-[var(--ink)] truncate">{stepDisplayLabel(step)}</span>
            {rowProgressLines.length > 0 && <span className="mt-0.5 block break-words text-[11px] text-[var(--nzr-muted)]">
              {rowProgressLines.map((line) => <span key={line} className="block">{line}</span>)}
            </span>}
          </span>
          <StatusChip status={visualStatus} />
          {step.attempt > 1 && <span className="text-[11px] text-[var(--nzr-muted)]">attempt {step.attempt.toLocaleString("en-GB")}</span>}
          {diagnostics.length > 0 && (
            <span className="text-[11px] text-[var(--nzr-muted)] tabular-nums">
              {diagnostics.length.toLocaleString("en-GB")} diagnostic{diagnostics.length === 1 ? "" : "s"}
            </span>
          )}
          <span className="flex-1" />
          {duration !== null && <span className="text-[11px] text-[var(--nzr-muted)] tabular-nums">{formatDuration(duration)}</span>}
        </button>
        <div className="px-3 pb-2" />
        {open && hasDetail && (
          <div className="flex flex-col gap-2 px-3 pb-3">
            {hasError && <ErrorBlock error={step.error} />}
            {progressLines.length > 0 && (
              <div className="rounded-sm border border-[var(--nzr-border)] bg-[var(--nzr-card)] px-3 py-1.5 text-[11px] text-[var(--nzr-muted)]">
                {progressLines.map((line) => <div key={line}>{line}</div>)}
              </div>
            )}
            {diagnostics.length > 0 && (
              <div className="rounded-sm border border-[var(--nzr-border)] bg-[var(--nzr-card)]">
                {diagnostics.map((d) => (
                  <DiagnosticRow key={d.eventId} diagnostic={d} hideStep />
                ))}
              </div>
            )}
            {logs.length > 0 && (
              <div className="rounded-sm border border-[var(--nzr-border)] bg-[var(--nzr-card)] px-3 py-1.5">
                {logs.map((e) => (
                  <LogLine key={e.eventId} event={e} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </TimelineNode>
  );
}

/** A run-level diagnostic (not bound to a step) as its own node on the feed. */
function DiagnosticNode({ diagnostic, isLast }: { diagnostic: DiagnosticSummary; isLast: boolean }) {
  return (
    <TimelineNode icon={<SeverityChip severity={diagnostic.severity} />} isLast={isLast}>
      <div className="-mt-0.5 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] text-[var(--ink)]">{diagnostic.code}</span>
          <span className="text-[11px] text-[var(--nzr-muted)]">{formatTimestamp(diagnostic.createdAt)}</span>
        </div>
        <div className="mt-0.5 whitespace-pre-wrap break-words text-[13px] text-[var(--ink)]">{diagnostic.message}</div>
      </div>
    </TimelineNode>
  );
}

/** A run-level event (not bound to a step) as a thin marker on the same rail. */
function EventMarker({ event, isLast }: { event: EventSummary; isLast: boolean }) {
  const time = event.createdAt ? new Date(event.createdAt).toLocaleTimeString() : "—";
  return (
    <TimelineNode icon={<span className="block h-2 w-2 rounded-full bg-[var(--border-soft)]" />} isLast={isLast}>
      <div className="flex items-center gap-2 flex-wrap py-0.5">
        <span className="font-mono text-[12px] text-[var(--ink)] truncate">{event.eventType}</span>
        <span className="flex-1" />
        <span className="tabular-nums text-[11px] text-[var(--nzr-muted)]">{time}</span>
      </div>
    </TimelineNode>
  );
}

type FeedNode =
  | { kind: "step"; time: number | null; step: StepSummary }
  | { kind: "diagnostic"; time: number | null; diagnostic: DiagnosticSummary }
  | { kind: "event"; time: number | null; event: EventSummary };

function useWorkflowStepAutoPagination({
  rootRef,
  sentinel,
  enabled,
  hasNextPage,
  isFetching,
  isError,
  loadNextPage,
}: {
  rootRef: { readonly current: Element | null } | null;
  sentinel: Element | null;
  enabled: boolean;
  hasNextPage: boolean;
  isFetching: boolean;
  isError: boolean;
  loadNextPage: () => void;
}) {
  useEffect(() => {
    if (!sentinel || !shouldAutoLoadWorkflowStepPage({ enabled, hasNextPage, isFetching, isError })) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadNextPage();
      },
      { root: rootRef?.current ?? null, rootMargin: "300px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, hasNextPage, isError, isFetching, loadNextPage, rootRef, sentinel]);
}

/**
 * The Diagnostics feed — a single vertical-spine timeline. Every step,
 * run-level diagnostic and run-level event is one node on the rail,
 * interleaved chronologically. Steps are accordions that nest their own
 * logs/diagnostics; step-bound events/diagnostics live inside their step.
 */
function DiagnosticsFeed({
  run,
  runId,
  steps,
  events,
  diagnostics,
  stepsTruncated,
  eventsTruncated,
  diagnosticsTruncated,
  now,
  stepStatuses,
  expansion,
  onToggleExpansion,
  confirmedPaused,
}: {
  run: RunSummary;
  runId: string;
  steps: StepSummary[];
  events: EventSummary[];
  diagnostics: DiagnosticSummary[];
  stepsTruncated: boolean;
  eventsTruncated: boolean;
  diagnosticsTruncated: boolean;
  now: number;
  stepStatuses: string[];
  expansion: WorkflowStepExpansion;
  onToggleExpansion: (key: string, current: boolean) => void;
  confirmedPaused: boolean;
}) {
  const stepsQuery = useWorkflowRunSteps(
    runId,
    isActiveWorkflowStatus(run.status) && !confirmedPaused,
    SOURCE,
    stepStatuses,
  );
  const pagedSteps = useMemo(
    () => mergeLatestWorkflowSteps(
      (stepsQuery.data?.pages ?? []).flatMap((page) => page.data),
      [],
    ),
    [stepsQuery.data],
  );
  const feedSteps = (stepStatuses.length > 0 ? mergeLiveWorkflowSteps(steps, pagedSteps) : steps).map((step) => projectCancelledWorkflowStep(step, run.status));

  const logsByStep = new Map<string, EventSummary[]>();
  const runEvents: EventSummary[] = [];
  for (const e of events) {
    if (e.correlationId) {
      const list = logsByStep.get(e.correlationId) ?? [];
      list.push(e);
      logsByStep.set(e.correlationId, list);
    } else {
      runEvents.push(e);
    }
  }

  const diagnosticsByStep = new Map<string, DiagnosticSummary[]>();
  const runDiagnostics: DiagnosticSummary[] = [];
  for (const d of diagnostics) {
    if (d.stepId) {
      const list = diagnosticsByStep.get(d.stepId) ?? [];
      list.push(d);
      diagnosticsByStep.set(d.stepId, list);
    } else {
      runDiagnostics.push(d);
    }
  }

  const nodes: FeedNode[] = [
    ...(stepStatuses.length > 0
      ? feedSteps.filter((step) => matchesWorkflowStepStatus(step.status, run.status, stepStatuses))
      : feedSteps
    ).map<FeedNode>((step) => ({ kind: "step", time: toMs(step.createdAt), step })),
    ...runDiagnostics.map<FeedNode>((diagnostic) => ({ kind: "diagnostic", time: toMs(diagnostic.createdAt), diagnostic })),
    ...runEvents.map<FeedNode>((event) => ({ kind: "event", time: toMs(event.createdAt), event })),
  ].sort((a, b) => {
    const entry = (node: FeedNode) => ({
      createdAt: node.kind === "step" ? node.step.createdAt : node.kind === "diagnostic" ? node.diagnostic.createdAt : node.event.createdAt,
      key: node.kind === "step" ? `step:${node.step.stepId}` : node.kind === "diagnostic" ? `diagnostic:${node.diagnostic.eventId}` : `event:${node.event.eventId}`,
    });
    return compareWorkflowCreation(entry(a), entry(b));
  });

  const loadedStepPages = stepsQuery.data?.pages.length ?? 0;
  const initialStepsLoading = stepsQuery.isLoading && loadedStepPages === 0;
  const loadingIncompleteSteps = stepStatuses.length > 0
    && !stepsQuery.isError
    && (stepsQuery.isLoading || stepsQuery.isFetchingNextPage || stepsQuery.isWarming);
  const incompleteStepsPending = stepStatuses.length > 0 && loadingIncompleteSteps;
  const incompleteStepsError = stepStatuses.length > 0 && stepsQuery.isError;
  const incompleteStepsErrorText = "Could not load matching steps.";
  const recoverySchedulerRef = useRef<WorkflowStepRecoveryScheduler | null>(null);
  if (!recoverySchedulerRef.current) recoverySchedulerRef.current = new WorkflowStepRecoveryScheduler();
  const stepsQueryRef = useRef(stepsQuery);
  stepsQueryRef.current = stepsQuery;
  useEffect(() => {
    if (!stepsQuery.isError) {
      return;
    }
    recoverySchedulerRef.current?.schedule(`${runId}:${stepStatuses.join(",")}`, () => {
      const query = stepsQueryRef.current;
      if (query.isFetching) return false;
      return (query.isFetchNextPageError ? query.fetchNextPage() : query.isHistoryError ? query.refetchHistory() : query.refetch({ cancelRefetch: true }))
        .then((result) => !result.isError);
    });
  }, [stepsQuery.isError, stepsQuery.isFetchNextPageError, runId, stepStatuses.join(",")]);
  useEffect(() => () => recoverySchedulerRef.current?.cancel(), [runId, stepStatuses.join(",")]);
  const [loadMoreNode, setLoadMoreNode] = useState<HTMLDivElement | null>(null);
  const loadNextPage = useCallback(() => {
    void stepsQuery.fetchNextPage({ cancelRefetch: false });
  }, [stepsQuery.fetchNextPage]);
  useWorkflowStepAutoPagination({
    rootRef: null,
    sentinel: loadMoreNode,
    enabled: true,
    hasNextPage: !!stepsQuery.hasNextPage,
    isFetching: stepsQuery.isFetching,
    isError: stepsQuery.isError,
    loadNextPage,
  });

  if (nodes.length === 0) {
    return (
      <section className="rounded-sm bg-[var(--nzr-card)] p-4 text-[13px] text-[var(--nzr-muted)]">
        {incompleteStepsError ? (
          <div className="flex items-center justify-between gap-3">
            <span>{incompleteStepsErrorText} Retrying automatically…</span>
          </div>
        ) : initialStepsLoading ? (
          "Loading saved steps…"
        ) : stepStatuses.length > 0 ? (
          stepsQuery.isWarming ? "Preparing matching steps…" : incompleteStepsPending ? "Loading matching steps…" : stepsQuery.hasNextPage ? (
            <>
              <span>No matching steps in the loaded page. Loading more matching steps…</span>
              <div ref={setLoadMoreNode} className="h-px" aria-hidden />
            </>
          ) : loadedStepPages > 0 ? "No steps match these statuses." : "Loading matching steps…"
        ) : stepsQuery.isWarming ? "Preparing steps…" : "No steps, diagnostics or events recorded for this run."}
        {stepStatuses.length === 0 && stepsQuery.hasNextPage && !stepsQuery.isError && <div ref={setLoadMoreNode} className="h-px" aria-hidden />}
      </section>
    );
  }

  const truncated = (stepStatuses.length > 0 ? stepsQuery.hasNextPage && !stepsQuery.isError : stepsTruncated) || eventsTruncated || diagnosticsTruncated;

  return (
    <section className="rounded-sm bg-[var(--nzr-card)] p-4">
      {truncated && (
        <div className="mb-3 -mx-4 -mt-4 px-3 py-2 text-[11px] text-[var(--orange)] bg-[var(--orange-bg)] border-b border-[var(--orange)] rounded-t-sm">
          Feed truncated — more entries exist than are shown.
        </div>
      )}
      {incompleteStepsError && (
        <div className="mb-3 -mx-4 -mt-4 px-3 py-2 text-[11px] text-[var(--red)] bg-[var(--red-bg)] border-b border-[var(--red)] rounded-t-sm flex items-center justify-between gap-3">
          <span>{incompleteStepsErrorText} Retrying automatically…</span>
        </div>
      )}
      {loadingIncompleteSteps && (
        <div className="mb-3 -mx-4 -mt-4 px-3 py-2 text-[11px] text-[var(--nzr-muted)] bg-[var(--card-2)] border-b border-[var(--nzr-border)] rounded-t-sm">
          {stepsQuery.isWarming ? "Preparing matching steps…" : "Loading matching steps…"}
        </div>
      )}
      <ol className="relative">
        {nodes.map((node, i) => {
          const isLast = i === nodes.length - 1;
          if (node.kind === "step")
            return (
              <StepNode
                key={`s-${node.step.stepId}`}
                step={node.step}
                logs={logsByStep.get(node.step.stepId) ?? []}
                diagnostics={diagnosticsByStep.get(node.step.stepId) ?? []}
                now={now}
                isLast={isLast}
                parentStatus={run.status}
                processingPaused={confirmedPaused}
                open={workflowStepExpanded(expansion, `step:${node.step.stepId}`, node.step.error !== null && node.step.error !== undefined)}
                onOpenChange={(next) => onToggleExpansion(`step:${node.step.stepId}`, !next)}
              />
            );
          if (node.kind === "diagnostic")
            return <DiagnosticNode key={`d-${node.diagnostic.eventId}`} diagnostic={node.diagnostic} isLast={isLast} />;
          return <EventMarker key={`e-${node.event.eventId}`} event={node.event} isLast={isLast} />;
        })}
      </ol>
      {stepsQuery.hasNextPage && !stepsQuery.isError && <div ref={setLoadMoreNode} className="h-px" aria-hidden />}
    </section>
  );
}

/** Bar fill for a step's status, in the shared status colour vocabulary. */
function barColour(status: string): string {
  const s = status.toLowerCase();
  if (s === "completed" || s === "succeeded") return "bg-[var(--green)]";
  if (s === "failed") return "bg-[var(--red)]";
  if (s === "running") return "bg-[var(--blue)]";
  if (s === "paused") return "bg-[var(--orange)]"
  if (s === "pending") return "bg-[var(--border-soft)]";
  return "bg-[var(--nzr-muted)]";
}

const TRACE_LABEL = "w-56";

type TreeNode = {
  key: string;
  label: string;
  depth: number;
  parent: string | null;
  collapsed: boolean;
  occ: Placed[];
  children: string[];
};

type Placed = {
  step: StepSummary;
  onAxis: boolean;
  leftPct: number;
  waitPct: number;
  execPct: number;
  offsetMs: number;
  durMs: number | null;
  waitMs: number | null;
};

/** One lane of the trace Gantt — status glyph + name on the left, its bar on the shared time track to the right. */
function GanttLane({
  placed,
  label,
  progressLines,
  status,
  depth,
  count,
  selected,
  hasChildren,
  expanded,
  childrenExpanded,
  rollup,
  onSelect,
  onToggle,
  onToggleChildren,
}: {
  placed: Placed;
  label: string;
  progressLines?: readonly string[] | null;
  status: string;
  depth: number;
  count: number;
  selected: boolean;
  hasChildren: boolean;
  expanded: boolean;
  childrenExpanded: boolean;
  rollup: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onToggleChildren: () => void;
}) {
  const { step, leftPct, waitPct, execPct, durMs, waitMs, offsetMs } = placed;
  const rawTotal = waitPct + execPct;
  const totalWidthPct = Math.max(0.5, Math.min(rawTotal, 100 - leftPct));
  const waitShare = rawTotal > 0 ? (waitPct / rawTotal) * 100 : 0;
  const tooltip =
    `${stepDisplayLabel(step)} · +${formatTrace(offsetMs)}` +
    (waitMs !== null ? ` · queued ${formatTrace(waitMs)}` : "") +
    (durMs !== null ? ` · ran ${formatTrace(durMs)}` : "");
  return (
    <div className={cn("flex w-full items-stretch border-t border-[var(--border-soft)] first:border-t-0", selected ? "bg-[var(--card-2)]" : "hover:bg-[var(--card-2)]")}>
      <div
        className={cn("shrink-0 sticky left-0 z-[1] flex items-start gap-1 pr-2 py-2.5 min-w-0 border-l-2", selected ? "border-[var(--nzr-border)] bg-[var(--card-2)]" : "border-transparent bg-[var(--nzr-card)]")}
        style={{ width: "var(--tl)" }}
      >
        <span className="shrink-0" style={{ width: `${(depth + 1) * 14 + 4}px` }} aria-hidden />
        {hasChildren ? (
          <>
            <button type="button" onClick={onToggle} aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`} className={styles.treeToggle}>
              <ChevronRight size={13} className={expanded ? "rotate-90 transition-transform" : "transition-transform"} />
            </button>
            <button
              type="button"
              onClick={onToggleChildren}
              aria-label={childrenExpanded ? `Collapse children of ${label}` : `Expand children of ${label}`}
              title={childrenExpanded ? "Collapse children" : "Expand children"}
              className={styles.treeChildrenToggle}
            >
              {childrenExpanded ? <ChevronsDownUp size={14} aria-hidden /> : <ChevronsUpDown size={14} aria-hidden />}
            </button>
          </>
        ) : (
          <span className="shrink-0 w-11" aria-hidden />
        )}
        <button type="button" onClick={onSelect} className="flex min-w-0 flex-1 items-start gap-2 text-left">
          <span className="flex h-5 shrink-0 items-center">
            <StatusIcon status={status} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block h-5 text-[13px] leading-5 font-medium text-[var(--ink)] truncate" title={label}>{label}</span>
            {progressLines?.length ? (
              <span className="mt-0.5 block text-[11px] text-[var(--nzr-muted)] tabular-nums">
                {progressLines.map((line, index) => <span key={`${index}-${line}`} className="block break-words">{line}</span>)}
              </span>
            ) : null}
          </span>
          {count > 1 && <span className="h-5 text-[11px] leading-5 text-[var(--nzr-muted)] shrink-0">×{count.toLocaleString("en-GB")}</span>}
          <span className="h-5 text-[11px] leading-5 text-[var(--nzr-muted)] tabular-nums shrink-0 pr-1">{durMs === null ? "—" : formatTrace(durMs)}</span>
        </button>
      </div>
      <button type="button" onClick={onSelect} className="relative flex-1 pr-3 py-2.5 cursor-pointer" aria-label={`Select ${label}`}>
        {!placed.onAxis ? (
          <span className="absolute left-0 top-5 -translate-y-1/2 text-[11px] text-[var(--nzr-muted)]">{untimedWorkflowStepLabel(status)}</span>
        ) : (
          <span className="absolute top-5 flex h-3.5 -translate-y-1/2 items-stretch" style={{ left: `${leftPct}%`, width: `${totalWidthPct}%`, minWidth: "3px" }} title={tooltip}>
            {waitShare > 0 && <span className="h-full rounded-l-sm bg-[var(--border-soft)]" style={{ width: `${waitShare}%` }} aria-hidden />}
            <span className={cn("h-full", barColour(status), waitShare > 0 ? "rounded-r-sm" : "rounded-sm", rollup && "opacity-40")} style={{ flex: "1 1 auto", minWidth: "2px" }} aria-hidden />
          </span>
        )}
      </button>
    </div>
  );
}

/** Detail panel for a selected trace span. */
function StepDetailPanel({
  step,
  logs,
  diagnostics,
  now,
  onClose,
  parentStatus,
  processingPaused,
}: {
  step: StepSummary;
  logs: EventSummary[];
  diagnostics: DiagnosticSummary[];
  now: number;
  onClose: () => void;
  parentStatus: string;
  processingPaused: boolean;
}) {
  const display = displayStepStatus(step, parentStatus);
  const visualStatus = processingPaused && isActiveWorkflowStatus(step.status) ? "paused" : display.status;
  const waitMs = elapsedMs(step.createdAt, step.startedAt, now);
  const runMs = elapsedMs(step.startedAt, stepEndAt(step), now);
  const hasError = step.error !== null && step.error !== undefined;
  const progressLines = workflowProgressLines(step.progress);
  const isEmpty = !hasError && diagnostics.length === 0 && logs.length === 0 && progressLines.length === 0;
  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-[var(--nzr-border)]">
      <div className="flex items-start gap-2 border-b border-[var(--nzr-border)] px-4 py-3">
        <span className="mt-0.5 shrink-0">
          <StatusIcon status={visualStatus} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-[var(--ink)] break-words">{stepDisplayLabel(step)}</div>
          <div className="mt-1">
            <StatusChip status={visualStatus} />
          </div>
        </div>
        <button type="button" onClick={onClose} className="shrink-0 rounded-xs p-1 text-[var(--nzr-muted)] hover:bg-[var(--card-2)] hover:text-[var(--ink)]" aria-label="Close step detail">
          <X size={16} />
        </button>
      </div>
      <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-auto px-4 py-3">
        <div className="grid grid-cols-2 gap-3">
          <HeaderField label="Queued" value={waitMs === null ? "—" : formatTrace(waitMs)} />
          <HeaderField label="Ran" value={runMs === null ? "—" : formatTrace(runMs)} />
          <HeaderField label="Attempt" value={step.attempt.toLocaleString("en-GB")} />
          <HeaderField label="Status" value={display.status} />
          {display.interrupted && <HeaderField label="Stored status" value={`${display.rawStatus} (parent ended)`} />}
          <HeaderField label="Created" value={formatTimestamp(step.createdAt)} />
          <HeaderField label="Started" value={formatTimestamp(step.startedAt)} />
          <HeaderField label="Updated" value={formatTimestamp(step.updatedAt)} />
          <HeaderField label="Executed by" value={step.executedByDeploymentId ?? "—"} />
          {progressLines.length > 0 && <HeaderField label="Progress" value={<div className="flex flex-col gap-1">{progressLines.map((line) => <span key={line} className="break-words">{line}</span>)}</div>} wide />}
          <HeaderField label="Step ID" value={<span className="font-mono">{step.stepId}</span>} wide />
        </div>
        {(["input", "output"] as const).map((field) => (
          <div key={field}>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--nzr-muted)]">
              {field === "input" ? "Input" : "Output"}
            </div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-sm border border-[var(--nzr-border)] bg-[var(--card-2)] px-3 py-2 text-[11px] text-[var(--ink)]">
              {step[field] == null ? "—" : JSON.stringify(step[field], null, 2)}
            </pre>
          </div>
        ))}
        {hasError && <ErrorBlock error={step.error} />}
        {diagnostics.length > 0 && (
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--nzr-muted)]">Diagnostics</div>
            <div className="rounded-sm border border-[var(--nzr-border)]">
              {diagnostics.map((d) => (
                <DiagnosticRow key={d.eventId} diagnostic={d} hideStep />
              ))}
            </div>
          </div>
        )}
        {logs.length > 0 && (
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--nzr-muted)]">Events</div>
            <div className="rounded-sm border border-[var(--nzr-border)] px-3 py-1.5">
              {logs.map((e) => (
                <LogLine key={e.eventId} event={e} />
              ))}
            </div>
          </div>
        )}
        {isEmpty && <p className="text-[12px] text-[var(--nzr-muted)]">No logs, diagnostics or error recorded for this step.</p>}
      </div>
    </aside>
  );
}

/** Detail panel for the selected RUN (the core workflow line). */
function RunDetailPanel({ run, workflowLabel, now, onClose }: { run: RunSummary; workflowLabel?: string; now: number; onClose: () => void }) {
  const duration = runElapsedMs(run, now);
  const hasError = run.error !== null && run.error !== undefined;
  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-[var(--nzr-border)]">
      <div className="flex items-start gap-2 border-b border-[var(--nzr-border)] px-4 py-3">
        <span className="mt-0.5 shrink-0">
          <StatusIcon status={run.status} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-[var(--ink)] break-words">{workflowLabel ?? workflowDisplayLabel(run)}</div>
          <div className="mt-1">
            <StatusChip
              status={run.status}
              pauseState={workflowRunPauseState(run)}
            />
          </div>
        </div>
        <button type="button" onClick={onClose} className="shrink-0 rounded-xs p-1 text-[var(--nzr-muted)] hover:bg-[var(--card-2)] hover:text-[var(--ink)]" aria-label="Close run detail">
          <X size={16} />
        </button>
      </div>
      <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-auto px-4 py-3">
        <div className="grid grid-cols-2 gap-3">
          <HeaderField label="Duration" value={duration === null ? "—" : formatTrace(duration)} />
          <HeaderField label="Status" value={run.status} />
          <HeaderField label="Triggered" value={run.triggeredBy ?? "—"} />
          <HeaderField label="Attempt" value={run.attempt == null ? "—" : run.attempt.toLocaleString("en-GB")} />
          <HeaderField label="Created" value={formatTimestamp(run.createdAt)} />
          <HeaderField label="Started" value={formatTimestamp(run.startedAt)} />
          <HeaderField label="Completed" value={formatTimestamp(run.completedAt)} />
          <HeaderField label="Deployment" value={run.deploymentId ?? "—"} />
          <HeaderField label="Run ID" value={<span className="font-mono">{run.runId}</span>} wide />
        </div>
        {hasError && <ErrorBlock error={run.error} />}
      </div>
    </aside>
  );
}

function formatTrace(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return formatDuration(ms);
}

function formatClock(ms: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(new Date(ms));
}

function BranchLoader({
  runId,
  active,
  source,
  statuses,
  parentKey,
  refreshActive,
  onChange,
}: {
  runId: string;
  active: boolean;
  source: string;
  statuses: readonly string[];
  parentKey: string;
  refreshActive: boolean;
  onChange: (parentKey: string, query: ReturnType<typeof useWorkflowRunStepBranch> | undefined) => void;
}) {
  const query = useWorkflowRunStepBranch(runId, active, source, statuses, parentKey, refreshActive);
  useEffect(() => {
    onChange(parentKey, query);
  }, [onChange, parentKey, query.data, query.error, query.fetchNextPage, query.hasNextPage, query.isError, query.isFetching, query.isLoading, query.refetch]);
  useEffect(() => () => onChange(parentKey, undefined), [onChange, parentKey]);
  return null;
}

/**
 * The trace timeline — a horizontal Gantt over the run's wall-clock.
 * Steps share one time axis: each is a lane whose bar is positioned by its
 * queue-start offset and split into a queued segment (created→started) and
 * an execution segment (started→ended). A ruler spans the track;
 * run-level events drop as vertical markers. Clicking a lane selects it
 * and opens its detail panel on the right. In-flight steps extend to `now`.
 */
function TraceGantt({ run, workflowLabel, runId, steps: detailSteps, tree, diagnostics, now, stepStatuses, expansion, onToggleExpansion, onToggleDescendantExpansion, confirmedPaused }: { run: RunSummary; workflowLabel?: string; runId: string; steps: StepSummary[]; tree?: RunDetailTree; diagnostics: DiagnosticSummary[]; now: number; stepStatuses: string[]; expansion: WorkflowStepExpansion; onToggleExpansion: (key: string, current: boolean) => void; onToggleDescendantExpansion: (key: string, expanded: boolean) => void; confirmedPaused: boolean }) {
  const traceChildrenId = useId();
  const runExpanded = workflowStepExpanded(expansion, WORKFLOW_RUN_TREE_KEY, true);
  const active = isActiveWorkflowStatus(run.status);
  const eventsQuery = useWorkflowRunEvents(runId, active && !confirmedPaused, SOURCE);
  const filteredRootQuery = useWorkflowRunSteps(runId, active && !confirmedPaused, SOURCE, stepStatuses, undefined, runExpanded && stepStatuses.length > 0, "tree");
  const rootSteps = useMemo(() => stepStatuses.length > 0
    ? (filteredRootQuery.data?.pages ?? []).flatMap((page) => page.data)
    : (tree ? workflowTreeRootSteps(tree) : detailSteps), [detailSteps, filteredRootQuery.data, stepStatuses.length, tree]);
  const [discoveredBranchSteps, setDiscoveredBranchSteps] = useState<StepSummary[]>([]);
  const branchKeys = useMemo(
    () => workflowVisibleBranchKeys(
      [...rootSteps, ...discoveredBranchSteps],
      (key) => workflowStepExpanded(expansion, `tree:${key}`, false),
    ),
    [discoveredBranchSteps, expansion, rootSteps],
  );
  type BranchQuery = ReturnType<typeof useWorkflowRunStepBranch> & { parentKey: string };
  const [branchQueryMap, setBranchQueryMap] = useState<Record<string, BranchQuery>>({});
  const updateBranchQuery = useCallback((parentKey: string, query: ReturnType<typeof useWorkflowRunStepBranch> | undefined) => {
    setBranchQueryMap((current) => {
      const next = { ...current };
      if (query) {
        const previous = current[parentKey];
        if (previous
          && previous.data === query.data
          && previous.error === query.error
          && previous.fetchNextPage === query.fetchNextPage
          && previous.hasNextPage === query.hasNextPage
          && previous.isError === query.isError
          && previous.isFetching === query.isFetching
          && previous.isLoading === query.isLoading
          && previous.refetch === query.refetch) return current;
        next[parentKey] = { ...query, parentKey };
      } else {
        if (!(parentKey in current)) return current;
        delete next[parentKey];
      }
      return next;
    });
  }, []);
  const branchQueries = branchKeys.map((parentKey) => branchQueryMap[parentKey]).filter((query): query is BranchQuery => query !== undefined);
  const branchSteps = useMemo(
    () => branchQueries.flatMap((query) => query.data?.pages.flatMap((page) => page.data) ?? []),
    [branchQueries],
  );
  useEffect(() => {
    setDiscoveredBranchSteps((current) => {
      if (
        current.length === branchSteps.length
        && current.every((step, index) => {
          const next = branchSteps[index];
          return next !== undefined
            && JSON.stringify({
              stepId: step.stepId,
              nodeKey: step.nodeKey,
              parentKey: step.parentKey,
              status: step.status,
              hasChildren: step.hasChildren,
              progress: step.progress,
              tree: step.tree,
            }) === JSON.stringify({
              stepId: next.stepId,
              nodeKey: next.nodeKey,
              parentKey: next.parentKey,
              status: next.status,
              hasChildren: next.hasChildren,
              progress: next.progress,
              tree: next.tree,
            });
        })
      ) return current;
      return branchSteps;
    });
  }, [branchSteps]);
  const steps = useMemo(
    () => mergeWorkflowTreePages(
      mergeLiveWorkflowSteps(rootSteps, []),
      [branchSteps],
    ).map((step) => projectCancelledWorkflowStep(step, run.status)),
    [branchSteps, rootSteps, run.status],
  );
  const events = useMemo(() => (eventsQuery.data?.pages ?? []).flatMap((p) => p.data), [eventsQuery.data]);
  const visibleSteps = useMemo(
    () =>
      stepStatuses.length > 0
        ? filterWorkflowTreeStepsByStatus(steps, run.status, stepStatuses)
        : steps,
    [run.status, stepStatuses, steps],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedKey = searchParams.get("node");
  const setSelected = (key: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key) next.set("node", key);
        else next.delete("node");
        return next;
      },
      { replace: true },
    );


  const runEvents = events.filter((e) => !e.correlationId);
  const [zoom, setZoom] = useState(1);
  const LABEL_MIN = 160;
  const LABEL_MAX = 560;
  const [labelW, setLabelW] = useState<number>(() => {
    const saved = Number(localStorage.getItem("nzr.wf.trace.labelW"));
    return Number.isFinite(saved) && saved >= LABEL_MIN ? Math.min(saved, LABEL_MAX) : 224;
  });
  const trackRef = useRef<HTMLDivElement | null>(null);
  const branchSentinelsRef = useRef(new Map<string, HTMLDivElement>());
  const branchSentinelCallbacksRef = useRef(new Map<string, (node: HTMLDivElement | null) => void>());
  const [branchSentinelVersion, setBranchSentinelVersion] = useState(0);
  const getBranchSentinelRef = useCallback((key: string) => {
    const existing = branchSentinelCallbacksRef.current.get(key);
    if (existing) return existing;
    const callback = (node: HTMLDivElement | null) => {
      const current = branchSentinelsRef.current.get(key);
      if (current === node) return;
      if (node) branchSentinelsRef.current.set(key, node);
      else branchSentinelsRef.current.delete(key);
      setBranchSentinelVersion((version) => version + 1);
    };
    branchSentinelCallbacksRef.current.set(key, callback);
    return callback;
  }, []);

  // Mod (Ctrl/Cmd) + wheel — and trackpad pinch, reported as ctrl-wheel —
  // zooms the track. Attached natively (non-passive) to preventDefault the
  // page/browser zoom; plain wheel still scrolls normally. Genuine DOM
  // event subscription, not state derived from props — a `useEffect` is
  // the correct tool here.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(8, Math.max(1, Math.round((z - e.deltaY * 0.01) * 10) / 10)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Edge-scroll shadows: these read live DOM scroll metrics (scrollLeft/
  // scrollWidth/clientWidth), which aren't derivable from props/state during
  // render — genuine DOM measurement, so an effect is the right tool, not a
  // derived-state anti-pattern.
  const [shadow, setShadow] = useState({ left: false, right: false });
  const updateShadows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setShadow({ left: el.scrollLeft > 1, right: Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 1 });
  }, []);
  useEffect(() => {
    updateShadows();
  }, [updateShadows, zoom, steps.length]);

  // Default the view to the active (most recent) work on first load.
  const didInitialScroll = useRef(false);
  useEffect(() => {
    if (didInitialScroll.current || steps.length === 0) return;
    didInitialScroll.current = true;
    requestAnimationFrame(() => {
      const el = trackRef.current;
      if (el) el.scrollLeft = el.scrollWidth;
    });
  }, [steps.length]);

  // The detail response supplies the visible roots. Each expanded branch
  // owns its cursor sentinel, so a slow or failed branch cannot block siblings.
  const branchHasNextPage = branchQueries.some((query) => !!query.hasNextPage);
  const branchIsFetching = branchQueries.some((query) => query.isFetching);
  const hasNextPage = branchHasNextPage || filteredRootQuery.hasNextPage;
  const isError = branchQueries.some((query) => query.isError) || filteredRootQuery.isError;
  const isFetchingNextPage = branchIsFetching || filteredRootQuery.isFetchingNextPage;
  const incompleteStepsErrorText = "Could not load matching steps.";
  const recoverySchedulerRef = useRef<WorkflowStepRecoveryScheduler | null>(null);
  if (!recoverySchedulerRef.current) recoverySchedulerRef.current = new WorkflowStepRecoveryScheduler();
  const recoveryQueriesRef = useRef({ branchQueries, filteredRootQuery });
  recoveryQueriesRef.current = { branchQueries, filteredRootQuery };
  useEffect(() => {
    if (!isError) return;
    const key = `${runId}:${stepStatuses.join(",")}:${branchQueries.filter((query) => query.isError).map((query) => query.parentKey).join(",")}`;
    recoverySchedulerRef.current?.schedule(key, () => {
      const { branchQueries: currentBranches, filteredRootQuery: currentRoot } = recoveryQueriesRef.current;
      const recoveries: Array<Promise<boolean>> = [
        ...currentBranches.filter((query) => query.isError && !query.isFetching).map((query) => query.retry()),
      ];
      if (currentRoot.isError && !currentRoot.isFetching) {
        recoveries.push((currentRoot.isFetchNextPageError
          ? currentRoot.fetchNextPage({ cancelRefetch: false })
          : currentRoot.isHistoryError
          ? currentRoot.refetchHistory()
          : currentRoot.refetch()
        ).then((result) => !result.isError));
      }
      return recoveries.length > 0 ? Promise.all(recoveries).then((results) => results.every(Boolean)) : false;
    });
  }, [isError, runId, stepStatuses.join(","), filteredRootQuery.isError, filteredRootQuery.isFetchNextPageError, branchQueries.some((query) => query.isError)]);
  useEffect(() => () => recoverySchedulerRef.current?.cancel(), [runId, stepStatuses.join(",")]);
  useEffect(() => {
    const sentinelRoot = trackRef.current;
    if (!sentinelRoot || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const parentKey = [...branchSentinelsRef.current.entries()]
            .find(([, node]) => node === entry.target)?.[0];
          const branch = parentKey === undefined
            ? undefined
            : branchQueries.find((query) => query.parentKey === parentKey);
          if (!branch || !branch.hasNextPage || branch.isFetching || branch.isError) continue;
          void branch.fetchNextPage({ cancelRefetch: false });
        }
      },
      { root: sentinelRoot, rootMargin: "500px" },
    );
    for (const [parentKey, node] of branchSentinelsRef.current) {
      const branch = branchQueries.find((query) => query.parentKey === parentKey);
      if (branch?.hasNextPage && !branch.isError) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [branchQueries, branchSentinelVersion]);

  const logsByStep = new Map<string, EventSummary[]>();
  for (const e of events) {
    if (!e.correlationId) continue;
    const list = logsByStep.get(e.correlationId) ?? [];
    list.push(e);
    logsByStep.set(e.correlationId, list);
  }
  const diagnosticsByStep = new Map<string, DiagnosticSummary[]>();
  for (const d of diagnostics) {
    if (!d.stepId) continue;
    const list = diagnosticsByStep.get(d.stepId) ?? [];
    list.push(d);
    diagnosticsByStep.set(d.stepId, list);
  }

  const bounds = visibleSteps.reduce<{ min: number; max: number } | null>((acc, step) => {
    const start = toMs(step.createdAt) ?? toMs(step.startedAt);
    if (start === null) return acc;
    const end = stepEndMs(step, now);
    const min = acc === null ? start : Math.min(acc.min, start);
    const max = acc === null ? end : Math.max(acc.max, end);
    return { min, max: Math.max(max, end) };
  }, null);

  const loadingIncompleteSteps = stepStatuses.length > 0
    && !isError
    && isFetchingNextPage;
  const warmingIncompleteSteps = stepStatuses.length > 0
    && (filteredRootQuery.isWarming || filteredRootQuery.data?.pages.some((page) =>
      page.status === "warming" || page.coverage === "warming",
    ) === true);
  if (stepStatuses.length > 0 && visibleSteps.length === 0 && runEvents.length === 0 && loadingIncompleteSteps) {
    return (
      <section className="rounded-sm bg-[var(--nzr-card)] p-4 text-[13px] text-[var(--nzr-muted)]">
        <span>Loading matching steps…</span>
      </section>
    );
  }
  if (visibleSteps.length === 0 && runEvents.length === 0 && stepStatuses.length > 0 && (filteredRootQuery.isLoading || warmingIncompleteSteps)) {
    return <section className="rounded-sm bg-[var(--nzr-card)] p-4 text-[13px] text-[var(--nzr-muted)]">Loading matching steps…</section>;
  }
  if (stepStatuses.length > 0 && filteredRootQuery.isError && visibleSteps.length === 0 && runEvents.length === 0) {
    return <section role="status" className="rounded-sm bg-[var(--nzr-card)] p-4 text-[13px] text-[var(--nzr-muted)]">Loading matching steps… retrying automatically</section>;
  }

  if (stepStatuses.length > 0 && visibleSteps.length === 0 && runEvents.length === 0 && isError) {
    return (
      <section className="rounded-sm bg-[var(--nzr-card)] p-4 text-[13px] text-[var(--nzr-muted)]">
        <div className="flex items-center justify-between gap-3">
          <span>{incompleteStepsErrorText} Retrying automatically…</span>
        </div>
      </section>
    );
  }

  if (stepStatuses.length > 0 && visibleSteps.length === 0 && runEvents.length === 0 && hasNextPage && !isError) {
    return (
      <section className="rounded-sm bg-[var(--nzr-card)] p-4 text-[13px] text-[var(--nzr-muted)]">
        {filteredRootQuery.hasNextPage ? (
          <Button variant="outline" onClick={() => void filteredRootQuery.fetchNextPage({ cancelRefetch: false })} disabled={filteredRootQuery.isFetchingNextPage}>
            {filteredRootQuery.isFetchingNextPage ? "Loading matching steps…" : "Load more matching steps"}
          </Button>
        ) : <span>Loading matching steps…</span>}
      </section>
    );
  }

  if (visibleSteps.length === 0 && runEvents.length === 0 && !hasNextPage && !isError && !warmingIncompleteSteps && (rootSteps.length > 0 || tree)) {
    return <section className="rounded-sm bg-[var(--nzr-card)] p-4 text-[13px] text-[var(--nzr-muted)]">{stepStatuses.length > 0 ? "No steps match these statuses." : "No trace recorded for this run."}</section>;
  }
  const loadMoreFilteredSteps = stepStatuses.length > 0 && filteredRootQuery.hasNextPage;

  const eventTimes = runEvents.map((e) => toMs(e.createdAt)).filter((n): n is number => n !== null);
  const t0 = bounds ? Math.min(bounds.min, ...eventTimes) : Math.min(...eventTimes, now);
  const tEnd = bounds ? Math.max(bounds.max, ...eventTimes) : Math.max(...eventTimes, now);
  const total = Math.max(1, tEnd - t0);

  const placed: Placed[] = visibleSteps.map((step) => {
    const createdMs = toMs(step.createdAt);
    const startedMs = toMs(step.startedAt);
    const updatedMs = stepEndMs(step, now);
    const left = createdMs ?? startedMs;
    if (left === null) return { step, onAxis: false, leftPct: 0, waitPct: 0, execPct: 0, offsetMs: 0, durMs: null, waitMs: null };
    const waitMs = createdMs === null ? null : Math.max(0, (startedMs ?? now) - createdMs);
    const durMs = startedMs === null ? null : Math.max(0, updatedMs - startedMs);
    const offsetMs = left - t0;
    return { step, onAxis: true, leftPct: (offsetMs / total) * 100, waitPct: waitMs !== null ? (waitMs / total) * 100 : 0, execPct: durMs !== null ? (durMs / total) * 100 : 0, offsetMs, durMs, waitMs };
  });
  placed.sort((a, b) => compareWorkflowStepsByCreation(a.step, b.step));

  const nodes = new Map<string, TreeNode>();
  const roots: string[] = [];
  for (const place of placed) {
    const chain = place.step.tree && place.step.tree.length > 0
      ? place.step.tree
      : [{ key: stepDisplayLabel(place.step), label: stepDisplayLabel(place.step) }];
    let parent: string | null = null;
    chain.forEach((meta, depth) => {
      const key = workflowTreePathKey(chain.slice(0, depth + 1).map((ancestor) => ancestor.key));
      let node = nodes.get(key);
      if (!node) {
        node = { key, label: meta.label, depth, parent, collapsed: !!meta.collapsed, occ: [], children: [] };
        nodes.set(key, node);
        if (parent === null) roots.push(key);
        else nodes.get(parent)?.children.push(key);
      } else if (meta.collapsed) {
        node.collapsed = true;
      }
      if (depth === chain.length - 1) node.occ.push(place);
      parent = key;
    });
  }
  for (const node of nodes.values()) node.occ.sort((a, b) => compareWorkflowStepsByCreation(a.step, b.step));

  const rollupOf = (key: string): { placed: Placed; status: string; rollup: boolean } | null => {
    const node = nodes.get(key);
    if (!node) return null;
    const descend: Placed[] = [];
    const statusesByNode: string[][] = [];
    const gather = (k: string) => {
      const n = nodes.get(k);
      if (!n) return;
      if (n.occ[0]) {
        descend.push(...n.occ);
        const latest = [...n.occ].sort((a, b) => compareWorkflowStepsNewestFirst(a.step, b.step))[0] ?? n.occ[0];
        statusesByNode.push([displayStepStatus(latest.step, run.status).status]);
      }
      n.children.forEach(gather);
    };
    gather(key);
    if (node.children.length === 0 && node.occ[0]) {
      const latest = [...node.occ].sort((a, b) => compareWorkflowStepsNewestFirst(a.step, b.step))[0] ?? node.occ[0];
      return { placed: latest, status: displayStepStatus(latest.step, run.status).status, rollup: false };
    }
    const status = aggregateLatestWorkflowNodeStatuses(statusesByNode);
    if (node.occ[0]) {
      const latest = [...node.occ].sort((a, b) => compareWorkflowStepsNewestFirst(a.step, b.step))[0] ?? node.occ[0];
      return { placed: latest, status, rollup: false };
    }
    const onAxis = descend.filter((p) => p.onAxis);
    if (onAxis.length === 0) {
      const rep = descend[0];
      return rep ? { placed: rep, status, rollup: true } : null;
    }
    const left = Math.min(...onAxis.map((p) => p.leftPct));
    const right = Math.max(...onAxis.map((p) => p.leftPct + p.waitPct + p.execPct));
    const rep = onAxis[0] as Placed;
    return {
      placed: { step: rep.step, onAxis: true, leftPct: left, waitPct: 0, execPct: Math.max(0.5, right - left), offsetMs: rep.offsetMs, durMs: null, waitMs: null },
      status,
      rollup: true,
    };
  };

  const genericProgressLines = (key: string): string[] | null => {
    const node = nodes.get(key);
    if (!node) return null;
    const latest = [...node.occ]
      .sort((a, b) => compareWorkflowStepsNewestFirst(a.step, b.step))
      .map(({ step }) => workflowProgressRowLines(step.progress))
      .find((lines) => lines.length > 0);
    return latest ?? null;
  };
  const progressLinesOf = (key: string): string[] | null => {
    const root = tree?.roots.find((candidate) => candidate.key === key);
    const rootLines = root ? workflowProgressRowLines(root.progress) : [];
    if (rootLines.length > 0) return rootLines;
    const stepLines = genericProgressLines(key);
    if (stepLines && stepLines.length > 0) return stepLines;
    return null;
  };

  const compareTreeKeys = workflowTreeOrder(steps);
  const visualRunStatus = confirmedPaused ? "paused" : run.status;
  const orderKeys = (keys: string[]) => [...keys].sort(compareTreeKeys);
  for (const node of nodes.values()) node.children = orderKeys(node.children);
  const orderedRoots = orderKeys(roots);

  const defaultCollapsedNode = (key: string): boolean => {
    const node = nodes.get(key);
    if (!node) return false;
    return node.collapsed || node.occ.some((p) => p.step.defaultCollapsed);
  };
  const isExpanded = (key: string): boolean => workflowStepExpanded(expansion, `tree:${key}`, !defaultCollapsedNode(key));
  const descendantBranchKeys = (key: string): string[] => {
    const branches: string[] = [];
    const visit = (childKey: string) => {
      const child = nodes.get(childKey);
      if (!child) return;
      if (child.children.length > 0) branches.push(childKey);
      child.children.forEach(visit);
    };
    nodes.get(key)?.children.forEach(visit);
    return branches;
  };
  const childrenExpanded = (key: string): boolean => {
    const branches = descendantBranchKeys(key);
    return branches.length > 0
      ? branches.every(isExpanded)
      : expansion.subtreeDefaults[`tree:${key}`] ?? true;
  };
  const runBranches = [...nodes.values()].filter((node) => node.children.length > 0);
  const runChildrenExpanded = runBranches.length > 0
    ? runBranches.every((node) => isExpanded(node.key))
    : expansion.subtreeDefaults[WORKFLOW_RUN_TREE_KEY] ?? true;

  type Row = { key: string; node: TreeNode; display: ReturnType<typeof rollupOf> };
  const rows: Row[] = [];
  const pushRows = (keys: string[]) => {
    for (const key of keys) {
      const node = nodes.get(key);
      if (!node) continue;
      rows.push({ key, node, display: rollupOf(key) });
      if (node.children.length > 0 && isExpanded(key)) pushRows(node.children);
    }
  };
  pushRows(orderedRoots);

  const selectedNode = selectedKey && selectedKey !== "run" ? nodes.get(selectedKey) ?? null : null;
  const selectedStep = selectedNode?.occ[0]?.step ?? null;

  const ticks = Array.from({ length: 5 }, (_, i) => ({
    pct: (i / 4) * 100,
    relative: formatTrace((i / 4) * total),
    clock: formatClock(t0 + (i / 4) * total),
    align: i === 0 ? "translate-x-0" : i === 4 ? "-translate-x-full" : "-translate-x-1/2",
  }));

  const ZOOM_MIN = 1;
  const ZOOM_MAX = 8;
  const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 10) / 10));

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = labelW;
    let finalW = startW;
    const onMove = (ev: MouseEvent) => {
      finalW = Math.min(LABEL_MAX, Math.max(LABEL_MIN, startW + (ev.clientX - startX)));
      setLabelW(finalW);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      localStorage.setItem("nzr.wf.trace.labelW", String(finalW));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <section className={cn(styles.timelineCard, "relative flex flex-1 min-h-0 flex-col")} style={{ ["--tl" as string]: `${labelW}px` } as React.CSSProperties}>
      {stepStatuses.length > 0 && isError && (
        <div className="flex items-center justify-between gap-3 border-b border-[var(--red)] bg-[var(--red-bg)] px-3 py-2 text-[11px] text-[var(--red)]">
          <span>{incompleteStepsErrorText} Retrying automatically…</span>
        </div>
      )}
      <div className="flex items-stretch flex-1 min-h-0">
        <div className="relative flex min-w-0 flex-1 min-h-0">
          <div ref={trackRef} onScroll={updateShadows} className="min-w-0 flex-1 min-h-0 overflow-auto">
            <div className="flex flex-col" style={{ width: `${zoom * 100}%`, minWidth: "100%" }}>
              <div className="sticky top-0 z-[3] flex items-stretch border-b border-[var(--nzr-border)] bg-[var(--nzr-card)]">
                <div className={cn("shrink-0 sticky left-0 z-[4] bg-[var(--nzr-card)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--nzr-muted)] flex items-center", TRACE_LABEL)} style={{ width: "var(--tl)" }}>
                  <span className="flex-1">Step</span>
                  <span role="separator" aria-orientation="vertical" onMouseDown={startResize} title="Drag to resize" className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-[var(--border-soft)]/70" />
                </div>
                <div className="relative flex-1 pr-3 h-9">
                  {ticks.map((t, i) => (
                    <span key={i} className={cn("absolute top-1 flex flex-col leading-tight text-[10px] tabular-nums", t.align)} style={{ left: `${t.pct}%` }}>
                      <span className="text-[var(--ink)]">{t.relative}</span>
                      <span className="text-[var(--nzr-muted)]">{t.clock}</span>
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 right-3" style={{ left: "var(--tl)" }}>
                  <div className="relative h-full">
                    {ticks.map((t, i) => (
                      <span key={`g-${i}`} className="absolute inset-y-0 border-l border-dashed border-[var(--nzr-border)]" style={{ left: `${t.pct}%` }} aria-hidden />
                    ))}
                    {runEvents.map((e) => {
                      const time = toMs(e.createdAt);
                      if (time === null) return null;
                      const left = ((time - t0) / total) * 100;
                      return (
                        <span key={`m-${e.eventId}`} className="pointer-events-auto absolute inset-y-0 border-l border-[var(--nzr-border)]" style={{ left: `${left}%` }} title={`${e.eventType} · ${formatTimestamp(e.createdAt)}`}>
                          <span className="block h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[var(--nzr-muted)]" />
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className={cn("flex w-full items-stretch border-t border-[var(--border-soft)] first:border-t-0", selectedKey === "run" ? "bg-[var(--card-2)]" : "hover:bg-[var(--card-2)]")}>
                  <div className={cn("shrink-0 sticky left-0 z-[1] flex items-center gap-1 pr-2 py-2.5 min-w-0 border-l-2 text-left", selectedKey === "run" ? "border-[var(--nzr-border)] bg-[var(--card-2)]" : "border-transparent bg-[var(--nzr-card)]")} style={{ width: "var(--tl)" }}>
                    <span className="shrink-0 w-1" aria-hidden />
                    {orderedRoots.length > 0 && (<>
                      <button
                        type="button"
                        onClick={() => onToggleExpansion(WORKFLOW_RUN_TREE_KEY, runExpanded)}
                        aria-label={`${runExpanded ? "Collapse" : "Expand"} run ${workflowLabel ?? workflowDisplayLabel(run)}`}
                        aria-expanded={runExpanded}
                        aria-controls={traceChildrenId}
                        className={styles.treeToggle}
                      >
                        <ChevronRight size={13} aria-hidden className={runExpanded ? "rotate-90 transition-transform" : "transition-transform"} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onToggleDescendantExpansion(WORKFLOW_RUN_TREE_KEY, !runChildrenExpanded)}
                        aria-label={`${runChildrenExpanded ? "Collapse" : "Expand"} children of run ${workflowLabel ?? workflowDisplayLabel(run)}`}
                        title={runChildrenExpanded ? "Collapse children" : "Expand children"}
                        className={styles.treeChildrenToggle}
                      >
                        {runChildrenExpanded ? <ChevronsDownUp size={14} aria-hidden /> : <ChevronsUpDown size={14} aria-hidden />}
                      </button>
                    </>)}
                    <button type="button" onClick={() => setSelected(selectedKey === "run" ? null : "run")} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                      <span className="shrink-0">
                        <StatusIcon status={visualRunStatus} />
                      </span>
                      <span className="truncate text-[12px] font-semibold text-[var(--ink)]">{workflowLabel ?? workflowDisplayLabel(run)}</span>
                    </button>
                  </div>
                  <button type="button" onClick={() => setSelected(selectedKey === "run" ? null : "run")} className="relative flex-1 pr-3 py-2.5 cursor-pointer" aria-label="Select run">
                    <span className="absolute top-1/2 left-0 flex h-3.5 -translate-y-1/2 items-stretch" style={{ width: "100%" }}>
                      <span className={cn("h-full rounded-sm", barColour(visualRunStatus))} style={{ flex: "1 1 auto" }} aria-hidden />
                    </span>
                  </button>
                </div>

                <div id={traceChildrenId} hidden={!runExpanded} className={rows.length > 0 ? "border-t border-[var(--border-soft)]" : undefined}>
                  {branchKeys.map((parentKey) => (
                    <BranchLoader key={parentKey} runId={runId} active={runExpanded} source={SOURCE} statuses={stepStatuses} parentKey={parentKey} refreshActive={active && !confirmedPaused} onChange={updateBranchQuery} />
                  ))}
                  {rows.map(({ key, node, display }) => {
                    if (!display) return null;
                    const progressLines = progressLinesOf(key);
                    const nodeHasChildren = node.children.length > 0
                      || node.occ.some(({ step }) => step.hasChildren === true);
                    const branchQuery = branchQueries.find((query) => query.parentKey === key);
                    return (
                      <div key={`node-${key}`}>
                        <GanttLane
                          placed={display.placed}
                          label={node.label}
                          progressLines={progressLines}
                          status={confirmedPaused && isActiveWorkflowStatus(display.status) ? "paused" : display.status}
                          depth={node.depth}
                          count={node.occ.length}
                          hasChildren={nodeHasChildren}
                          expanded={isExpanded(key)}
                          childrenExpanded={childrenExpanded(key)}
                          rollup={display.rollup}
                          selected={selectedKey === key}
                          onSelect={() => setSelected(selectedKey === key ? null : key)}
                          onToggle={() => onToggleExpansion(`tree:${key}`, isExpanded(key))}
                          onToggleChildren={() => onToggleDescendantExpansion(`tree:${key}`, !childrenExpanded(key))}
                        />
                        {branchQuery?.isLoading && (
                          <div
                            role="status"
                            className={WORKFLOW_TREE_CHILD_LOADING_CLASS}
                            style={{ paddingLeft: workflowTreeChildLoadingPadding(node.depth) }}
                          >
                            <Loader2 size={13} className="shrink-0 animate-spin" aria-hidden />
                            <span>Loading visible children…</span>
                          </div>
                        )}
                        {branchQuery?.isError && (
                          <div className="flex items-center gap-2 border-t border-[var(--border-soft)] px-3 py-1.5 text-[11px] text-[var(--red)]">
                            <span role="status">Could not load children. Retrying automatically…</span>
                          </div>
                        )}
                        {branchQuery?.hasNextPage && !branchQuery.isError && (
                          <div
                            ref={getBranchSentinelRef(key)}
                            className="h-px"
                            aria-hidden
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div aria-hidden style={{ left: "var(--tl)" }} className={cn("pointer-events-none absolute inset-y-0 w-3 bg-gradient-to-r from-[var(--scroll-shadow-color)] to-transparent transition-opacity duration-150", shadow.left ? "opacity-100" : "opacity-0")} />
          <div aria-hidden className={cn("pointer-events-none absolute inset-y-0 right-0 w-3 bg-gradient-to-l from-[var(--scroll-shadow-color)] to-transparent transition-opacity duration-150", shadow.right ? "opacity-100" : "opacity-0")} />
        </div>

        {selectedKey === "run" && <RunDetailPanel run={run} workflowLabel={workflowLabel} now={now} onClose={() => setSelected(null)} />}
        {selectedStep && (
          <StepDetailPanel step={selectedStep} logs={logsByStep.get(selectedStep.stepId) ?? []} diagnostics={diagnosticsByStep.get(selectedStep.stepId) ?? []} now={now} parentStatus={run.status} processingPaused={confirmedPaused} onClose={() => setSelected(null)} />
        )}
      </div>

      {loadMoreFilteredSteps && <Button variant="outline" onClick={() => void filteredRootQuery.fetchNextPage({ cancelRefetch: false })} disabled={filteredRootQuery.isFetchingNextPage}>{filteredRootQuery.isFetchingNextPage ? "Loading matching steps…" : "Load more matching steps"}</Button>}
      <div className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1">
        <button type="button" onClick={() => setZoom((z) => clampZoom(z - 0.5))} disabled={zoom <= ZOOM_MIN} aria-label="Zoom out" className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-[var(--nzr-border)] bg-[var(--nzr-card)] text-[var(--nzr-muted)] shadow-sm hover:bg-[var(--card-2)] disabled:opacity-40">
          −
        </button>
        <button type="button" onClick={() => setZoom(1)} aria-label="Reset zoom" className="pointer-events-auto flex h-7 items-center justify-center rounded-full border border-[var(--nzr-border)] bg-[var(--nzr-card)] px-2 text-[11px] tabular-nums text-[var(--nzr-muted)] shadow-sm hover:bg-[var(--card-2)]">
          {zoom.toFixed(1)}×
        </button>
        <button type="button" onClick={() => setZoom((z) => clampZoom(z + 0.5))} disabled={zoom >= ZOOM_MAX} aria-label="Zoom in" className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-[var(--nzr-border)] bg-[var(--nzr-card)] text-[var(--nzr-muted)] shadow-sm hover:bg-[var(--card-2)] disabled:opacity-40">
          +
        </button>
      </div>
    </section>
  );
}

/** Run-detail tab selector — published into the shell's secondary-header slot. */
function RunTabs({
  tab,
  tabHref,
  attempts,
  currentRunId,
  attemptHref,
  diagnosticCount,
  stepStatuses,
  onStepStatusesChange,
  statusOptions,
}: {
  tab: Tab;
  tabHref: (t: Tab) => string;
  attempts: Array<{ runId: string; attempt: number; status: string; createdAt: string | null }>;
  currentRunId: string;
  attemptHref: (runId: string) => string;
  diagnosticCount: number | null;
  stepStatuses: string[];
  onStepStatusesChange: (values: string[]) => void;
  statusOptions: WorkflowFilterOption[];
}) {
  const navigate = useNavigate();
  const orderedAttempts = [...attempts].sort((a, b) => b.attempt - a.attempt);
  const currentAttempt = orderedAttempts.find((a) => a.runId === currentRunId);
  const latestAttempt = orderedAttempts[0];
  return (
    <div className={styles.tabBarRow}>
      <div className={styles.tabBarStart}>
        {attempts.length > 1 && (
          <SingleSelectDropdown
            label="Select run attempt"
            triggerLabel={`Attempt ${currentAttempt?.attempt.toLocaleString("en-GB") ?? "current"}${currentAttempt?.runId === latestAttempt?.runId ? " (latest)" : ""}`}
            options={orderedAttempts.map((a, index) => ({
                value: a.runId,
                label: `Attempt ${a.attempt.toLocaleString("en-GB")} (${a.status})${index === 0 ? " (latest)" : ""}`,
              }))}
            selectedValue={currentRunId}
            onValueChange={(value) => navigate(attemptHref(value))}
          />
        )}
        <div className={styles.tabBar} role="tablist" aria-label="Run detail views">
          {TABS.map((t) => (
            <Link
              key={t.id}
              to={tabHref(t.id)}
              role="tab"
              aria-selected={tab === t.id}
              className={cn(styles.tab, tab === t.id && styles.tabActive)}
            >
              {t.label}
              {t.id === "diagnostics" && diagnosticCount !== null && diagnosticCount > 0 && (
                <span className="ml-1.5 tabular-nums text-[var(--nzr-muted)]">({diagnosticCount.toLocaleString("en-GB")})</span>
              )}
            </Link>
          ))}
        </div>
      </div>
      {(tab === "timeline" || tab === "diagnostics") && (
        <div className={styles.toolbarGroup}>
          <WorkflowFilterDropdown
            options={statusOptions}
            values={stepStatuses}
            onChange={onStepStatusesChange}
            label="Filter steps by status"
            emptyLabel="All step statuses"
          />
        </div>
      )}
    </div>
  );
}

export function WorkflowRunDetailPage() {
  const { runId = "" } = useParams<{ runId: string }>();
  const location = useLocation();
  const segment = location.pathname.replace(/^\/workflows\/runs\/[^/]+\/?/, "").split("/")[0];
  const tab: Tab = segment === "diagnostics" || segment === "artefacts" || segment === "logs" ? segment : "timeline";
  useDocumentTitle(`Run ${runId.slice(0, 12)}`);

  const [searchParams, setSearchParams] = useSearchParams();
  const stepStatuses = useMemo(() => readWorkflowStepStatuses(searchParams), [searchParams]);
  const query = useWorkflowRunDetail(runId, SOURCE);
  useWorkflowRunDetailStream(runId, SOURCE);
  const definitionsQuery = useWorkflowDefinitions();
  const workflowProgressQuery = useWorkflowRunProgress(
    runId,
    SOURCE,
    { scope: "run" },
    true,
    query.data ? isActiveWorkflowStatus(query.data.run.status) : false,
  );
  const [retryingRunDetails, setRetryingRunDetails] = useState(false);
  const { refetch: refetchRunDetails } = query;
  const recoverySchedulerRef = useRef<WorkflowStepRecoveryScheduler | null>(null);
  if (!recoverySchedulerRef.current) recoverySchedulerRef.current = new WorkflowStepRecoveryScheduler();
  const queryRef = useRef(query);
  queryRef.current = query;
  useEffect(() => {
    if (!query.isError) { setRetryingRunDetails(false); return; }
    setRetryingRunDetails(true);
    recoverySchedulerRef.current?.schedule(runId, async () => {
      const current = queryRef.current;
      if (current.isFetching) return false;
      const result = await current.refetch({ cancelRefetch: true });
      return !result.isError;
    });
  }, [query.isError, runId]);
  useEffect(() => () => recoverySchedulerRef.current?.cancel(), [runId]);
  const setStepStatuses = useCallback((values: string[]) => {
    setSearchParams((current) => withWorkflowStepStatuses(current, values));
  }, [setSearchParams]);
  const [expansion, setExpansion] = useState<WorkflowStepExpansion>(emptyWorkflowStepExpansion);
  useEffect(() => setExpansion(emptyWorkflowStepExpansion()), [runId]);
  const toggleExpansion = useCallback((key: string, current: boolean) => {
    setExpansion((previous) => withWorkflowStepExpansionOverride(previous, key, !current));
  }, []);
  const toggleDescendantExpansion = useCallback((key: string, expanded: boolean) => {
    setExpansion((previous) => withWorkflowStepDescendantExpansion(previous, key, expanded));
  }, []);
  const liveNow = useNow();
  const confirmedPaused = query.data ? workflowRunConfirmedPaused(query.data.run) : false;
  const now = runClockMs(query.data?.run, liveNow);

  const counts = query.data?.diagnosticCounts;
  const diagnosticCount = counts ? counts.warn + counts.error : null;

  // `useSecondaryHeader` requires a referentially stable node (a fresh JSX
  // literal every render re-fires its effect every render — an infinite
  // loop, per that hook's own doc comment) — memoise on the real
  // dependencies, not on `tabHref` itself (a fresh closure every render).
  const tabBar = useMemo(() => {
    const tabHref = (t: Tab) => workflowRunTabHref(runId, t, searchParams);
    return (
      <RunTabs
        tab={tab}
        tabHref={tabHref}
        attempts={query.data?.attempts ?? []}
        currentRunId={runId}
        attemptHref={(attemptRunId) => workflowRunTabHref(attemptRunId, tab, searchParams)}
        diagnosticCount={diagnosticCount}
        stepStatuses={stepStatuses}
        onStepStatusesChange={setStepStatuses}
        statusOptions={STEP_STATUS_OPTIONS}
      />
    );
  }, [tab, diagnosticCount, runId, query.data?.attempts, searchParams, setStepStatuses, stepStatuses]);
  useSecondaryHeader(tabBar);

  if (query.isLoading) {
    return (
      <div className={styles.page}>
        <section className={styles.headerCard}>
          <SkeletonBlock height={120} width="100%" />
        </section>
      </div>
    );
  }
  if (!query.data) {
    return (
      <div className={styles.page}>
        <section className={styles.emptyCard}>
          <p className={styles.emptyState} role="alert">Couldn't load this workflow run.</p>
          {query.isError && <p className={styles.emptyState}>{workflowRunLoadErrorMessage(query.error)}</p>}
          <p className={styles.emptyState} role="status">{retryingRunDetails ? "Retrying automatically…" : "Loading automatically…"}</p>
        </section>
      </div>
    );
  }

  const { run, steps, tree, events, diagnostics, diagnosticCounts, stepStatusCounts, durableWaitCounts } = query.data;
  const definition = definitionsQuery.data?.definitions.find((d) => d.name === run.workflowName) ?? null;

  return (
    <div className={cn(styles.page, tab === "timeline" && styles.timelinePage)}>
      {query.isError && (
        <div className={styles.loadErrorBanner} role="alert">
          <span>{workflowRunLoadErrorMessage(query.error)} Showing the last loaded run details.</span>
          <span role="status">{retryingRunDetails ? "Retrying automatically…" : "Loading automatically…"}</span>
        </div>
      )}

      <RunHeader
        run={run}
        counts={diagnosticCounts}
        stepStatusCounts={stepStatusCounts ?? null}
        durableWaitCounts={durableWaitCounts ?? null}
        workflowProgress={workflowProgressQuery.data}
        workflowProgressLoading={workflowProgressQuery.isLoading}
        workflowProgressError={workflowProgressQuery.isError}
        workflowProgressRetry={() => { void workflowProgressQuery.refetch(); }}
        now={now}
        definition={definition}
      />

      <RunFailureBanner run={run} />

      {tab === "timeline" && <TraceGantt run={run} workflowLabel={definition?.label} runId={runId} steps={steps} tree={tree} diagnostics={diagnostics} now={now} stepStatuses={stepStatuses} expansion={expansion} onToggleExpansion={toggleExpansion} onToggleDescendantExpansion={toggleDescendantExpansion} confirmedPaused={confirmedPaused} />}
      {tab === "diagnostics" && (
        <DiagnosticsFeed
          run={run}
          runId={runId}
          steps={steps}
          events={events}
          diagnostics={diagnostics}
          stepsTruncated={query.data.stepsTruncated}
          eventsTruncated={query.data.eventsTruncated}
          diagnosticsTruncated={query.data.diagnosticsTruncated}
          now={now}
          stepStatuses={stepStatuses}
          expansion={expansion}
          onToggleExpansion={toggleExpansion}
          confirmedPaused={confirmedPaused}
        />
      )}
      {tab === "artefacts" && <ArtefactsPanel runId={runId} source={SOURCE} />}
      {tab === "logs" && <LogsTab runId={runId} source={SOURCE} status={run.status} />}
    </div>
  );
}
